import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAssignableOwners, reassignClientOwner } from '../../shared/api/clients.api'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { Spinner } from '../../shared/ui/Spinner'
import { ROLE_LABELS } from '@crm/shared'

interface Props {
  clientId: string
  clientName: string
  currentOwnerId: string
  /** Кол-во активных заявок клиента (из загруженной карточки) — для превью. */
  activeRequestsCount: number
  onClose: () => void
}

export function ReassignOwnerModal({
  clientId,
  clientName,
  currentOwnerId,
  activeRequestsCount,
  onClose,
}: Props) {
  const queryClient = useQueryClient()
  const [newOwnerId, setNewOwnerId] = useState('')
  const [transferActive, setTransferActive] = useState(true)

  const { data: owners, isLoading } = useQuery({
    queryKey: ['clients', 'assignable-owners'],
    queryFn: getAssignableOwners,
  })

  // Текущего владельца из списка убираем — передавать самому себе нельзя.
  const options = useMemo(
    () => (owners ?? []).filter((o) => o.id !== currentOwnerId),
    [owners, currentOwnerId],
  )

  const mutation = useMutation({
    mutationFn: () =>
      reassignClientOwner(clientId, {
        newOwnerId,
        transferActiveRequests: transferActive,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      onClose()
    },
  })

  const canSubmit = newOwnerId !== '' && !mutation.isPending

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-xl shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Сменить владельца</h2>
          <p className="text-sm text-gray-500 mt-0.5">Клиент «{clientName}»</p>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Новый владелец</label>
          {isLoading ? (
            <div className="py-2">
              <Spinner />
            </div>
          ) : (
            <select
              autoFocus
              value={newOwnerId}
              onChange={(e) => setNewOwnerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">— выберите менеджера —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {(o.name || o.email) + ` · ${ROLE_LABELS[o.role]}`}
                </option>
              ))}
            </select>
          )}
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={transferActive}
            onChange={(e) => setTransferActive(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-gray-700">
            Перенести активные заявки на нового владельца
            <span className="block text-xs text-gray-500 mt-0.5">
              Будет передано {activeRequestsCount} активных заявок. Закрытые заявки останутся за
              прошлым владельцем (KPI и история сохранятся за ним), но новый владелец сможет их
              видеть.
            </span>
          </span>
        </label>

        {mutation.isError && <ErrorMessage error={mutation.error} />}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Передача...' : 'Передать клиента'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  )
}
